/**
 * Booking State Persistence Service
 * 
 * Manages saving and restoring booking progress across app sessions.
 * Works with localStorage for cross-platform compatibility (Web, PWA, iOS, Android WebView).
 */

import Api from'./api';
import axios from'axios';

const BOOKING_STATE_KEY ='uttaapp_unfinished_booking_state';
const BOOKING_STATE_VERSION ='v1';

// Early booking routes where resume modal is allowed
const EARLY_BOOKING_ROUTES = ['/book-ride','/nearby-drivers','/waiting-for-drivers'];

// Routes where resume modal should NOT show (booking already confirmed/started)
const BLOCKED_ROUTES = ['/booking-confirmation'];

// Booking statuses that allow resume modal (early stages only)
const ALLOWED_STATUSES = ['pending','negotiating'];

// Booking statuses that block resume modal (driver assigned or beyond)
const BLOCKED_STATUSES = ['accepted','confirmed','driver_arrived','in_progress','completed','cancelled','rejected'];

/**
 * Check if form data contains meaningful booking information
 * @param {Object} routeState - Route state containing formData, paymentMethod, vehicleType, step
 * @returns {boolean} True if there's meaningful booking data
 */
const hasMeaningfulBookingData = (routeState) => {
 if (!routeState) {
 return false;
 }

 const formData = routeState.formData || {};
 const paymentMethod = routeState.paymentMethod;
 const vehicleType = routeState.vehicleType;
 const step = routeState.step;

 // Helper function to safely check if a string value is non-empty
 const isNonEmptyString = (value) => {
 return value && typeof value ==='string'&& value.trim().length > 0;
 };

 // Check if user has entered pickup location (meaningful data)
 // Requires all three: location, locationName, and pincode
 const hasPickupLocation = isNonEmptyString(formData.pickupLocation) &&
 isNonEmptyString(formData.pickupLocationName) &&
 isNonEmptyString(formData.pickupPincode);

 // Check if user has entered dropoff location (meaningful data)
 const hasDropoffLocation = isNonEmptyString(formData.dropoffLocation);

 // Check if user has selected payment method (meaningful progress)
 const hasPaymentMethod = isNonEmptyString(paymentMethod);

 // Check if user has selected vehicle type (meaningful progress)
 const hasVehicleType = isNonEmptyString(vehicleType);

 // Check if user has progressed beyond location step (meaningful progress)
 // If step is'payment','vehicle', or anything other than'location', it's meaningful
 const hasProgressedBeyondLocation = step && 
 typeof step ==='string'&& 
 step.trim().length > 0 && 
 step !=='location';

 // Return true if any meaningful data exists
 const hasMeaningfulData = hasPickupLocation || 
 hasDropoffLocation || 
 hasPaymentMethod || 
 hasVehicleType || 
 hasProgressedBeyondLocation;

 if (!hasMeaningfulData) {
 console.log('📋 No meaningful booking data found:', {
 hasPickupLocation,
 hasDropoffLocation,
 hasPaymentMethod,
 hasVehicleType,
 hasProgressedBeyondLocation,
 step,
 pickupLocation: formData.pickupLocation ?'exists':'missing',
 dropoffLocation: formData.dropoffLocation ?'exists':'missing'});
 }

 return hasMeaningfulData;
};

/**
 * Save booking state to localStorage
 * @param {Object} state - Booking state object
 * @param {string} state.bookingId - Optional booking ID
 * @param {string} state.currentStep - Current step in booking flow
 * @param {string} state.lastRoute - Last route user was on
 * @param {Object} state.routeState - State to pass to route navigation
 * @param {number} state.timestamp - Timestamp when state was saved
 */
export const saveBookingState = (state) => {
 try {
 const bookingState = {
 version: BOOKING_STATE_VERSION,
 bookingId: state.bookingId || null,
 currentStep: state.currentStep || null,
 lastRoute: state.lastRoute || null,
 routeState: state.routeState || null,
 timestamp: state.timestamp || Date.now(),
 savedAt: Date.now()
 };

 localStorage.setItem(BOOKING_STATE_KEY, JSON.stringify(bookingState));
 console.log('💾 Booking state saved:', {
 route: bookingState.lastRoute,
 step: bookingState.currentStep,
 timestamp: new Date(bookingState.timestamp).toISOString()
 });
 return true;
 } catch (error) {
 console.error('❌ Error saving booking state:', error);
 return false;
 }
};

/**
 * Get saved booking state from localStorage
 * @returns {Object|null} Booking state or null if not found/invalid
 */
export const getBookingState = () => {
 try {
 const stored = localStorage.getItem(BOOKING_STATE_KEY);
 if (!stored) {
 return null;
 }

 const state = JSON.parse(stored);
 
 // Validate state structure
 if (!state || typeof state !=='object') {
 return null;
 }

 // Check if state is stale (older than 24 hours)
 const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
 const stateAge = Date.now() - (state.timestamp || state.savedAt || 0);
 if (stateAge > TWENTY_FOUR_HOURS_MS) {
 console.log('⏰ Booking state is stale (older than 24 hours), clearing...');
 clearBookingState();
 return null;
 }

 return state;
 } catch (error) {
 console.error('❌ Error reading booking state:', error);
 return null;
 }
};

/**
 * Clear saved booking state
 */
export const clearBookingState = () => {
 try {
 localStorage.removeItem(BOOKING_STATE_KEY);
 console.log('🗑️ Booking state cleared');
 return true;
 } catch (error) {
 console.error('❌ Error clearing booking state:', error);
 return false;
 }
};

/**
 * Check if there's an unfinished booking
 * This function only checks localStorage - does not verify with backend
 * @returns {boolean} True if unfinished booking state exists in localStorage
 */
export const hasUnfinishedBooking = () => {
 const state = getBookingState();
 return state !== null && state.lastRoute !== null;
};

/**
 * Check if booking is in early stage and resume modal should be shown
 * @param {string} bookingId - Optional booking ID to check status
 * @returns {Promise<boolean>} True if booking is in early stage and modal should show
 */
export const shouldShowResumeModal = async (bookingId = null) => {
 const state = getBookingState();
 
 // No saved state = no modal
 if (!state || !state.lastRoute) {
 return false;
 }

 // Block if route is booking-confirmation or beyond
 if (BLOCKED_ROUTES.some(route => state.lastRoute.startsWith(route))) {
 console.log('📋 Route is blocked for resume modal:', state.lastRoute);
 clearBookingState();
 return false;
 }

 // Only allow early booking routes
 if (!EARLY_BOOKING_ROUTES.some(route => state.lastRoute.startsWith(route))) {
 console.log('📋 Route is not an early booking route:', state.lastRoute);
 clearBookingState();
 return false;
 }

 // CRITICAL: Check if there's meaningful booking data before showing modal
 // Don't show modal if user just opened the form without entering any data
 if (!hasMeaningfulBookingData(state.routeState)) {
 console.log('📋 No meaningful booking data found - not showing resume modal');
 clearBookingState();
 return false;
 }

 // If no bookingId, it's still in draft/flow stage - allow resume (but only if meaningful data exists)
 if (!bookingId) {
 console.log('📋 No bookingId - still in booking flow with meaningful data, allowing resume');
 return true;
 }

 // If bookingId exists, check status from backend
 try {
 const token = localStorage.getItem('uttaapp_token');
 if (!token) {
 console.log('📋 No auth token - clearing state');
 clearBookingState();
 return false;
 }

 const response = await axios.get(Api.Booking.getBooking(bookingId), {
 headers: {'Authorization':`Bearer ${token}`}
 });

 if (response.data && response.data.success && response.data.booking) {
 const status = response.data.booking.status;
 
 // Block if status is in blocked list
 if (BLOCKED_STATUSES.includes(status)) {
 console.log('📋 Booking status is blocked:', status,'- clearing state');
 clearBookingState();
 return false;
 }
 
 // Allow if status is in allowed list
 if (ALLOWED_STATUSES.includes(status)) {
 console.log('📋 Booking status is allowed:', status,'- showing resume modal');
 return true;
 }
 
 // Unknown status - treat as blocked (safer)
 console.log('⚠️ Unknown booking status:', status,'- treating as blocked');
 clearBookingState();
 return false;
 }

 // Booking not found - clear state
 console.log('📋 Booking not found - clearing state');
 clearBookingState();
 return false;
 } catch (error) {
 // If booking not found (404), clear state
 if (error.response?.status === 404) {
 console.log('📋 Booking not found (404) - clearing state');
 clearBookingState();
 return false;
 }
 
 // For network errors, don't show modal (safer to not resume on network issues)
 if (error.code ==='ERR_NETWORK'|| error.message?.includes('CONNECTION_REFUSED') || error.message?.includes('Network Error')) {
 console.warn('⚠️ Network error checking booking status - not showing resume modal');
 return false;
 }
 
 console.error('❌ Error checking booking status:', error);
 // On other errors, don't show modal (safer)
 return false;
 }
};

/**
 * Save booking state when route changes
 * @param {string} route - Current route path
 * @param {Object} routeState - State to pass to route navigation
 * @param {string} step - Current step (optional)
 * @param {string} bookingId - Booking ID (optional)
 */
export const saveBookingRoute = (route, routeState = null, step = null, bookingId = null) => {
 // Only save if it's an EARLY booking route (not booking-confirmation)
 // Resume modal should NOT work after booking is confirmed
 if (!EARLY_BOOKING_ROUTES.some(r => route.startsWith(r))) {
 // If route is booking-confirmation or beyond, clear state instead of saving
 if (BLOCKED_ROUTES.some(r => route.startsWith(r))) {
 console.log('📋 Route is beyond early stage - clearing booking state');
 clearBookingState();
 }
 return false;
 }

 return saveBookingState({
 bookingId,
 currentStep: step,
 lastRoute: route,
 routeState,
 timestamp: Date.now()
 });
};

/**
 * Save booking step change (for multi-step flows like BookRidePage)
 * @param {string} route - Current route path
 * @param {string} step - Current step
 * @param {Object} routeState - State to pass to route navigation
 */
export const saveBookingStep = (route, step, routeState = null) => {
 // CRITICAL: Only save booking state if there's meaningful booking data
 // Don't save if user just opened the form without entering any data
 if (!hasMeaningfulBookingData(routeState)) {
 console.log('📋 No meaningful booking data - not saving booking state');
 return false;
 }

 return saveBookingRoute(route, routeState, step);
};


